import type { BrowserContext, SdkVersionEntry } from "@quotientjs/core"
import { createFingerprint } from "@quotientjs/browser-fingerprint"
import {
  BrowserStorageProvider,
  StorageProvider,
} from "@quotientjs/browser-storage"

import { BrowserApiClient } from "./browser-api-client"

import { generateUUID } from "./uuid"

import { Audience } from "./clients/audience"
import { People } from "./clients/people"
import { Analytics } from "./clients/analytics"
import { Auth } from "./clients/auth"
import { Blog } from "./clients/blog"
import { Flow } from "./clients/flow"
import { BrowserContextManager } from "./clients/browser-context"
import type { Store } from "./clients/store"

import { DeviceIdStore } from "./stores/device-id"
import { BrowserFingerprintStore } from "./stores/browser-fingerprint"

export interface QuotientClientOptions {
  apiKey: string
  baseUrl?: string
  /**
   * SDK packages wrapping this client (e.g. @quotientjs/react), outermost
   * first. Forwarded to the API client, which reports them together with
   * this package and @quotientjs/core on every request via the
   * X-Quotient-Sdk-Version header.
   */
  sdkVersions?: SdkVersionEntry[]
  /**
   * Whether to automatically track page views on init and on navigation
   * (`pushState`/`replaceState`/`popstate`). Default: true.
   *
   * Set to false if something else on the page already tracks page views, to
   * avoid double-counting.
   */
  autoTrackPageViews?: boolean
}

/**
 * Browser client for analytics, authentication, audience management, and
 * other Quotient APIs. Construction does not require asynchronous setup.
 */
export class QuotientClient {
  public auth: Auth
  public analytics: Analytics
  public audience: Audience
  /** @deprecated Use `audience.people` instead */
  public people: People
  public blog: Blog
  public flow: Flow
  public store: Store
  protected storageProvider: StorageProvider
  protected apiClient: BrowserApiClient
  protected browserContext: BrowserContextManager

  constructor(
    storageProvider: StorageProvider,
    apiKey: string,
    baseUrl?: string,
    sdkVersions?: SdkVersionEntry[],
  ) {
    this.storageProvider = storageProvider
    this.apiClient = new BrowserApiClient({
      apiKey: apiKey,
      baseUrl: baseUrl,
      sdkVersions: sdkVersions,
    })

    this.apiClient.setStorageProvider(this.storageProvider)

    // Analytics events must see IDs returned by People without reading stale
    // browser storage, so every client shares the one BrowserContextManager and
    // its Store.
    this.browserContext = new BrowserContextManager(this.storageProvider)
    this.store = this.browserContext.store

    this.auth = new Auth(this.apiClient)
    this.analytics = new Analytics(this.apiClient, this.browserContext)
    this.audience = new Audience(
      this.apiClient,
      this.store,
      this.browserContext,
    )
    this.people = this.audience.people
    this.blog = new Blog(this.apiClient)
    this.flow = new Flow(this.apiClient)
  }

  /**
   * Creates a browser client and starts page-view tracking unless it is
   * disabled in the options.
   */
  static init(options: QuotientClientOptions): QuotientClient {
    const storageProvider = new BrowserStorageProvider()

    // Initialize with device ID
    const deviceId = DeviceIdStore.get(storageProvider)
    if (!deviceId) {
      const newDeviceId = generateUUID()
      DeviceIdStore.set(storageProvider, newDeviceId)
    }

    // Initialize with browser fingerprint
    const fingerprint = BrowserFingerprintStore.get(storageProvider)
    if (!fingerprint) {
      // Generate fingerprint in the background - don't block client creation
      createFingerprint({
        context: {
          window: window,
          document: document,
          navigator: navigator,
        },
      })
        .then((newFingerprint) => {
          BrowserFingerprintStore.set(storageProvider, newFingerprint.hash)
        })
        .catch((error) => {
          console.error("Failed to generate browser fingerprint:", error)
        })
    }

    const client = new QuotientClient(
      storageProvider,
      options.apiKey,
      options.baseUrl,
      options.sdkVersions,
    )
    if (options.autoTrackPageViews !== false) {
      client.analytics.initPageViewTracking()
    }
    return client
  }

  /**
   * Returns what the SDK currently knows about the visitor: device, session,
   * person, referrer, and attribution. Send it in the body of a request to a
   * server on another domain, where the `qt_browser_context` cookie doesn't
   * travel, so that server can attribute the event it records. Same-site
   * servers can read the cookie instead.
   */
  getBrowserContext(): BrowserContext {
    return this.browserContext.prepare()
  }

  /**
   * Stops this client from tracking navigation. Call when the client is no
   * longer in use (e.g. a React provider unmounting) so it doesn't keep
   * sending events for pages it no longer owns.
   */
  dispose(): void {
    this.analytics.disablePageViewTracking()
  }
}
